import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { worksheetAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Home, Search, Eye, Copy, Download, X, FileText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const WorksheetLibrary = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [preview, setPreview] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await worksheetAPI.library();
      setGroups(data.groups || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load worksheet library');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => g.class_name.toLowerCase().includes(q) || g.subject.toLowerCase().includes(q) || g.worksheets.some((w) => (w.chapter || '').toLowerCase().includes(q)));
  }, [groups, search]);

  const handleUse = async (id) => {
    setBusyId(id);
    try {
      const { data } = await worksheetAPI.duplicate(id);
      toast.success('Added to your worksheets');
      navigate(`/worksheet/${data.worksheet_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to use worksheet');
    } finally { setBusyId(null); }
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const handleDownload = async (w, fmt) => {
    setBusyId(w.worksheet_id + fmt);
    try {
      const api = fmt === 'docx' ? worksheetAPI.downloadDocx : worksheetAPI.downloadPdf;
      const { data } = await api(w.worksheet_id, false);
      const safe = (w.chapter || w.subject || 'worksheet').replace(/[^a-z0-9-_ ]/gi, '').slice(0, 40).trim() || 'worksheet';
      downloadBlob(new Blob([data]), `${safe}.${fmt}`);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch {
      toast.error('Download failed');
    } finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFCFB] via-white to-orange-50/40">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button data-testid="home-button" variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#C53030]"><Home className="w-5 h-5 mr-2" /> Home</Button>
          <div className="flex items-center gap-2"><FileText className="w-6 h-6 text-[#C53030]" /><h1 className="text-xl font-bold text-[#1C1917]">Worksheet Library</h1></div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8 flex flex-wrap justify-between items-end gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C53030] mb-2 font-semibold">Worksheet Library</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1C1917] font-bold">Ready-to-Print Worksheets</h2>
            <p className="text-base text-stone-600 mt-2">{total} finalized worksheets shared anonymously by teachers</p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input data-testid="worksheet-library-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search class, subject, chapter..." className="pl-9 bg-white/80" />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C53030] mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center border border-stone-200 bg-white/60">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 mx-auto mb-6 flex items-center justify-center shadow-lg"><Sparkles className="w-10 h-10 text-white" /></div>
            <h3 className="text-xl font-semibold text-[#1C1917] mb-2">No worksheets yet</h3>
            <p className="text-stone-600">Finalized worksheets from all teachers will appear here.</p>
          </Card>
        ) : (
          <div className="space-y-10">
            {filtered.map((g) => (
              <section key={`${g.class_name}-${g.subject}`} data-testid={`ws-group-${g.class_name}-${g.subject}`}>
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-xs uppercase tracking-widest text-[#C53030] bg-red-50 px-3 py-1 rounded-full font-semibold">{g.class_name}</span>
                  <h3 className="text-lg font-semibold text-[#1C1917]">{g.subject}</h3>
                  <span className="text-sm text-stone-500">({g.worksheets.length})</span>
                  <div className="flex-1 h-px bg-gradient-to-r from-stone-200 to-transparent" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {g.worksheets.map((w) => {
                    const busyDocx = busyId === w.worksheet_id + 'docx';
                    const busyPdf = busyId === w.worksheet_id + 'pdf';
                    const totalQs = w.questions?.length || 0;
                    return (
                      <Card key={w.worksheet_id} data-testid={`ws-card-${w.worksheet_id}`} className="group relative overflow-hidden border border-stone-200 bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                        <div className="h-24 bg-gradient-to-br from-amber-500 to-orange-500 relative">
                          <FileText className="absolute right-4 top-4 w-10 h-10 text-white/90" />
                          <div className="absolute bottom-3 left-4 text-white">
                            <p className="text-xs uppercase tracking-wider opacity-90">{w.class_name}</p>
                            <p className="text-sm font-semibold">{w.subject}</p>
                          </div>
                        </div>
                        <div className="p-5">
                          <p className="font-bold text-[#1C1917] mb-1 line-clamp-2 min-h-[2.5rem]">{w.chapter || 'Untitled'}</p>
                          <div className="flex items-center gap-2 text-xs text-stone-500 mb-4">
                            <span>{totalQs} Q{totalQs !== 1 ? 's' : ''}</span><span>·</span>
                            <span>{w.max_marks} marks</span>
                            {w.board && <><span>·</span><span>{w.board}</span></>}
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <Button data-testid={`ws-view-${w.worksheet_id}`} size="sm" variant="outline" onClick={() => setPreview(w)} className="border border-stone-200 text-xs"><Eye className="w-3 h-3 mr-1" /> View</Button>
                            <Button data-testid={`ws-use-${w.worksheet_id}`} size="sm" onClick={() => handleUse(w.worksheet_id)} disabled={busyId === w.worksheet_id} className="bg-[#C53030] hover:bg-[#9B2C2C] text-white text-xs"><Copy className="w-3 h-3 mr-1" /> Use</Button>
                            <div className="relative group/dl">
                              <Button data-testid={`ws-download-${w.worksheet_id}`} size="sm" variant="outline" disabled={busyDocx || busyPdf} className="border border-stone-200 text-xs w-full"><Download className="w-3 h-3 mr-1" /> {(busyDocx || busyPdf) ? '...' : 'Download'}</Button>
                              <div className="absolute right-0 top-full mt-1 hidden group-hover/dl:block z-10 bg-white border border-stone-200 rounded-md shadow-lg overflow-hidden">
                                <button data-testid={`ws-dl-pdf-${w.worksheet_id}`} onClick={() => handleDownload(w, 'pdf')} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-stone-50">PDF</button>
                                <button data-testid={`ws-dl-docx-${w.worksheet_id}`} onClick={() => handleDownload(w, 'docx')} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-stone-50">DOCX</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="ws-preview-modal" className="max-w-6xl h-[90vh] p-0">
          <DialogHeader className="p-4 border-b border-stone-200 flex-row items-center justify-between">
            <DialogTitle className="text-base">{preview?.chapter || 'Worksheet'} — <span className="text-stone-500 font-normal">{preview?.subject} · {preview?.class_name}</span></DialogTitle>
            <Button variant="ghost" size="sm" onClick={() => setPreview(null)}><X className="w-4 h-4" /></Button>
          </DialogHeader>
          {preview && <iframe data-testid="ws-preview-iframe" title="Worksheet PDF" src={worksheetAPI.inlinePdfUrl(preview.worksheet_id)} className="w-full h-full border-0" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorksheetLibrary;
